import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { formatLocalDate } from './dateUtils';

/**
 * Exporta transacciones a PDF
 */
export const exportToPDF = (transactions, categories, startDate, endDate, userName = 'Usuario') => {
  const doc = new jsPDF();
  
  // Header
  doc.setFontSize(20);
  doc.setTextColor(51, 51, 51);
  doc.text('Portal de Gastos | Reporte', 14, 20);
  
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text(`Generado: ${new Date().toLocaleDateString('es-MX')}`, 14, 28);
  doc.text(`Usuario: ${userName}`, 14, 33);
  
  if (startDate && endDate) {
    doc.text(`Período: ${startDate} - ${endDate}`, 14, 38);
  }
  
  // Resumen
  const totalIngresos = transactions
    .filter(t => t.type === 'income')
    .reduce((sum, t) => sum + t.amount, 0);
  
  const totalGastos = transactions
    .filter(t => t.type === 'expense')
    .reduce((sum, t) => sum + t.amount, 0);
  
  const balance = totalIngresos - totalGastos;
  
  doc.setFontSize(12);
  doc.setTextColor(51, 51, 51);
  doc.text('Resumen Financiero', 14, 48);
  
  doc.setFontSize(10);
  doc.setTextColor(22, 163, 74); // green
  doc.text(`Ingresos: $${totalIngresos.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`, 14, 55);
  
  doc.setTextColor(239, 68, 68); // red
  doc.text(`Gastos: $${totalGastos.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`, 14, 61);
  
  doc.setTextColor(balance >= 0 ? 22 : 239, balance >= 0 ? 163 : 68, balance >= 0 ? 74 : 68);
  doc.text(`Balance: $${balance.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`, 14, 67);
  
  // Tabla de transacciones
  const tableData = transactions.map(t => {
    const category = categories.find(c => c.id === t.category_id);
    return [
      t.date ? formatLocalDate(t.date) : '-',
      t.description || '-',
      category?.name || 'Sin categoría',
      t.type === 'income' ? 'Ingreso' : 'Gasto',
      `$${t.amount.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`
    ];
  });
  
  autoTable(doc, {
    startY: 75,
    head: [['Fecha', 'Descripción', 'Categoría', 'Tipo', 'Monto']],
    body: tableData,
    theme: 'striped',
    headStyles: {
      fillColor: [79, 70, 229], // indigo
      textColor: 255,
      fontStyle: 'bold'
    },
    styles: {
      fontSize: 9,
      cellPadding: 3
    },
    columnStyles: {
      0: { cellWidth: 25 },
      1: { cellWidth: 60 },
      2: { cellWidth: 35 },
      3: { cellWidth: 25 },
      4: { cellWidth: 30, halign: 'right' }
    }
  });
  
  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.text(
      `Página ${i} de ${pageCount}`,
      doc.internal.pageSize.getWidth() / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
    doc.text(
      'Generado por Portal de Gastos | pgastos.capacitatu.com',
      doc.internal.pageSize.getWidth() / 2,
      doc.internal.pageSize.getHeight() - 6,
      { align: 'center' }
    );
  }
  
  // Guardar
  const fileName = `reporte_gastos_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
};

/**
 * Exporta transacciones a Excel con formato ejecutivo y tabla estilizada (sin dependencias externas)
 */
export const exportToExcel = (transactions, categories, startDate, endDate, userName = 'Usuario', periodLabel = null) => {
  const totalIngresos = transactions
    .filter(t => t.type === 'income')
    .reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);

  const totalGastos = transactions
    .filter(t => t.type === 'expense')
    .reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);

  const balanceNeto = totalIngresos - totalGastos;
  const periodoTexto = periodLabel || (startDate && endDate ? `${startDate} al ${endDate}` : 'Historial Completo');
  const fechaGeneracion = new Date().toLocaleString('es-MX', { dateStyle: 'long', timeStyle: 'short' });
  const balanceColor = balanceNeto >= 0 ? '#166534' : '#991B1B';

  const rowsHtml = transactions.map((t, idx) => {
    const category = categories.find(c => c.id === t.category_id);
    const dateFormatted = t.date ? formatLocalDate(t.date) : '';
    const tipoTexto = t.type === 'income' ? 'Ingreso' : t.type === 'saving' ? 'Ahorro' : 'Gasto';
    const monto = parseFloat(t.amount) || 0;
    const metodo = t.account_name || t.payment_method || (t.account_id ? 'Cuenta vinculada' : 'No especificado');
    const rowBg = idx % 2 === 0 ? '#FFFFFF' : '#F8FAFC';
    const tipoColor = t.type === 'income' ? '#16A34A' : t.type === 'saving' ? '#2563EB' : '#DC2626';

    return `
      <tr style="background-color:${rowBg};">
        <td style="border:1px solid #E2E8F0; padding:6px 12px; font-size:10pt; text-align:center; color:#334155;">${dateFormatted}</td>
        <td style="border:1px solid #E2E8F0; padding:6px 12px; font-size:10pt; font-weight:bold; color:#0F172A;">${t.description || 'Sin descripción'}</td>
        <td style="border:1px solid #E2E8F0; padding:6px 12px; font-size:10pt; color:#334155;">${category?.name || 'Sin categoría'}</td>
        <td style="border:1px solid #E2E8F0; padding:6px 12px; font-size:10pt; text-align:center; font-weight:bold; color:${tipoColor};">${tipoTexto}</td>
        <td style="border:1px solid #E2E8F0; padding:6px 12px; font-size:10pt; text-align:right; font-weight:bold;">$${monto.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
        <td style="border:1px solid #E2E8F0; padding:6px 12px; font-size:10pt; color:#64748B;">${metodo}</td>
        <td style="border:1px solid #E2E8F0; padding:6px 12px; font-size:10pt; color:#64748B; font-style:italic;">${t.notes || ''}</td>
      </tr>`;
  }).join('');

  const excelTemplate = `
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
<head>
  <meta http-equiv="Content-Type" content="text/html; charset=utf-8">
  <!--[if gte mso 9]><xml>
    <x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>
      <x:Name>Transacciones</x:Name>
      <x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
    </x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook>
  </xml><![endif]-->
</head>
<body>
  <table style="border-collapse:collapse; font-family:Calibri,Arial,sans-serif;">

    <!-- BANNER AZUL ÚNICO -->
    <tr>
      <td colspan="7" style="background-color:#4338CA; border:none; padding:18px 22px; vertical-align:middle;">
        <span style="font-size:20pt; font-weight:bold; color:#FFFFFF;">Portal de Gastos</span>
        &nbsp;&nbsp;
        <span style="font-size:10pt; color:rgba(255,255,255,0.70); font-style:italic;">Reporte de Transacciones &middot; ${new Date().getFullYear()}</span>
        &nbsp;&nbsp;&nbsp;
        <span style="font-size:9pt; color:#FFFFFF; font-weight:bold; background-color:rgba(255,255,255,0.18); padding:3px 12px; border-radius:10px;">PRO REPORT</span>
      </td>
    </tr>
    <!-- Línea acento -->
    <tr>
      <td colspan="7" style="background-color:#6366F1; height:3px; padding:0; border:none;"></td>
    </tr>
    <!-- URL tagline -->
    <tr>
      <td colspan="7" style="background-color:#4338CA; border:none; padding:2px 22px 12px 22px;">
        <span style="font-size:8pt; color:rgba(255,255,255,0.55);">pgastos.capacitatu.com</span>
      </td>
    </tr>

    <!-- METADATOS -->
    <tr>
      <td colspan="2" style="font-size:9pt; color:#64748B; padding:7px 14px; border:none; border-bottom:1px solid #E2E8F0; background-color:#F8FAFC;"><strong>Usuario:</strong> ${userName}</td>
      <td colspan="2" style="font-size:9pt; color:#64748B; padding:7px 14px; border:none; border-bottom:1px solid #E2E8F0; background-color:#F8FAFC;"><strong>Período:</strong> ${periodoTexto}</td>
      <td colspan="3" style="font-size:9pt; color:#64748B; padding:7px 14px; border:none; border-bottom:1px solid #E2E8F0; background-color:#F8FAFC;"><strong>Generado:</strong> ${fechaGeneracion}</td>
    </tr>

    <!-- Separador -->
    <tr><td colspan="7" style="border:none; height:8px; background-color:#F8FAFC;"></td></tr>

    <!-- RESUMEN FINANCIERO -->
    <tr>
      <td colspan="7" style="font-size:9pt; font-weight:bold; color:#475569; padding:9px 14px 6px 14px; letter-spacing:1px; text-transform:uppercase; border:none; border-bottom:2px solid #4F46E5; background-color:#F1F5F9;">RESUMEN FINANCIERO DEL LOTE</td>
    </tr>
    <tr>
      <td style="background-color:#DCFCE7; color:#166534; font-weight:bold; border:1px solid #86EFAC; padding:8px 12px; font-size:9pt;">Total Ingresos:</td>
      <td style="background-color:#DCFCE7; color:#166534; font-weight:bold; border:1px solid #86EFAC; padding:8px 12px; text-align:right; font-size:11pt;">$${totalIngresos.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
      <td style="background-color:#FEE2E2; color:#991B1B; font-weight:bold; border:1px solid #FCA5A5; padding:8px 12px; font-size:9pt;">Total Gastos:</td>
      <td style="background-color:#FEE2E2; color:#991B1B; font-weight:bold; border:1px solid #FCA5A5; padding:8px 12px; text-align:right; font-size:11pt;">$${totalGastos.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
      <td style="background-color:#EFF6FF; color:#1E40AF; font-weight:bold; border:1px solid #93C5FD; padding:8px 12px; font-size:9pt;">Balance Neto:</td>
      <td style="background-color:#EFF6FF; color:${balanceColor}; font-weight:bold; border:1px solid #93C5FD; padding:8px 12px; text-align:right; font-size:11pt;">$${balanceNeto.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
      <td style="background-color:#F5F3FF; border:1px solid #C4B5FD; padding:8px 12px; text-align:center; font-size:11pt; font-weight:bold; color:#4F46E5;">${transactions.length} movimientos</td>
    </tr>

    <!-- Separador -->
    <tr><td colspan="7" style="border:none; height:10px;"></td></tr>

    <!-- CABECERA TABLA -->
    <thead>
      <tr>
        <th style="background-color:#4F46E5; color:#FFFFFF; font-weight:bold; font-size:10pt; padding:10px 12px; border:1px solid #4338CA; text-align:center; width:110px;">Fecha</th>
        <th style="background-color:#4F46E5; color:#FFFFFF; font-weight:bold; font-size:10pt; padding:10px 12px; border:1px solid #4338CA; text-align:left; width:250px;">Descripción</th>
        <th style="background-color:#4F46E5; color:#FFFFFF; font-weight:bold; font-size:10pt; padding:10px 12px; border:1px solid #4338CA; text-align:left; width:150px;">Categoría</th>
        <th style="background-color:#4F46E5; color:#FFFFFF; font-weight:bold; font-size:10pt; padding:10px 12px; border:1px solid #4338CA; text-align:center; width:100px;">Tipo</th>
        <th style="background-color:#4F46E5; color:#FFFFFF; font-weight:bold; font-size:10pt; padding:10px 12px; border:1px solid #4338CA; text-align:right; width:140px;">Monto ($ MXN)</th>
        <th style="background-color:#4F46E5; color:#FFFFFF; font-weight:bold; font-size:10pt; padding:10px 12px; border:1px solid #4338CA; text-align:left; width:180px;">Método / Cuenta</th>
        <th style="background-color:#4F46E5; color:#FFFFFF; font-weight:bold; font-size:10pt; padding:10px 12px; border:1px solid #4338CA; text-align:left; width:210px;">Notas</th>
      </tr>
    </thead>

    <!-- DATOS -->
    <tbody>
      ${rowsHtml}
    </tbody>

    <!-- FOOTER -->
    <tr>
      <td colspan="7" style="font-size:8pt; color:#94A3B8; padding:10px 14px; border:none; border-top:1px solid #E2E8F0; font-style:italic; background-color:#F8FAFC;">
        Generado por Portal de Gastos &middot; pgastos.capacitatu.com &middot; ${new Date().getFullYear()} — Documento confidencial, uso exclusivo del titular.
      </td>
    </tr>

  </table>
</body>
</html>`;

  const blob = new Blob([excelTemplate], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  const fileName = `reporte_gastos_${new Date().toISOString().split('T')[0]}.xls`;
  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Exporta transacciones a CSV
 */
export const exportToCSV = (transactions, categories) => {
  const data = transactions.map(t => {
    const category = categories.find(c => c.id === t.category_id);
    return {
      'Fecha': t.date ? formatLocalDate(t.date) : '',
      'Descripción': t.description || '',
      'Categoría': category?.name || 'Sin categoría',
      'Tipo': t.type === 'income' ? 'Ingreso' : 'Gasto',
      'Monto': t.amount,
      'Método de Pago': t.payment_method || '',
      'Notas': t.notes || ''
    };
  });
  
  const ws = XLSX.utils.json_to_sheet(data);
  const csv = XLSX.utils.sheet_to_csv(ws);
  
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `transacciones_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Calcula la regla 50-30-20
 */
export const calculate503020Rule = (transactions, categories) => {
  const expenses = transactions.filter(t => t.type === 'expense');
  const totalExpenses = expenses.reduce((sum, t) => sum + t.amount, 0);
  
  if (totalExpenses === 0) {
    return {
      needs: 0,
      wants: 0,
      savings: 0,
      needsPercentage: 0,
      wantsPercentage: 0,
      savingsPercentage: 0
    };
  }
  
  // Categorías de necesidades (estas deberían ser configurables)
  const needsCategories = ['Alimentación', 'Vivienda', 'Transporte', 'Salud', 'Servicios'];
  const wantsCategories = ['Entretenimiento', 'Restaurantes', 'Compras', 'Viajes'];
  
  let needs = 0;
  let wants = 0;
  
  expenses.forEach(t => {
    const category = categories.find(c => c.id === t.category_id);
    if (category) {
      if (needsCategories.includes(category.name)) {
        needs += t.amount;
      } else if (wantsCategories.includes(category.name)) {
        wants += t.amount;
      }
    }
  });
  
  const income = transactions
    .filter(t => t.type === 'income')
    .reduce((sum, t) => sum + t.amount, 0);
  
  const savings = income - totalExpenses;
  
  return {
    needs,
    wants,
    savings: Math.max(0, savings),
    needsPercentage: income > 0 ? (needs / income) * 100 : 0,
    wantsPercentage: income > 0 ? (wants / income) * 100 : 0,
    savingsPercentage: income > 0 ? (savings / income) * 100 : 0,
    ideal: {
      needs: income * 0.5,
      wants: income * 0.3,
      savings: income * 0.2
    }
  };
};
